/**
 * Service Worker bootstrap.
 *
 * Two modes selected at build time by `REACT_APP_PWA_ENABLED`:
 *
 * ─ Mode A: KILL-SWITCH (default; flag unset or "false") ─
 *   Registers /sw.js, which wipes every cache + unregisters itself.
 *   Listens for the `SW_KILLED` postMessage and reloads once to pick
 *   up a fresh, un-cached bundle. Belt-and-braces purge from the page
 *   if any registration survived.  This keeps the original iter138
 *   kill-switch behaviour for users who haven't drained the v1 cache
 *   yet.
 *
 * ─ Mode B: PWA (iter167) ─
 *   Registers /sw-v3.js, which precaches the app shell + uses
 *   network-first for navigations and cache-first for hashed
 *   /static/ assets. API calls are NEVER cached. Sweeps obsolete
 *   caches on activate so a downgrade from a future v4 doesn't
 *   leave dead bytes around.
 *
 * Flip the flag in /app/frontend/.env when you're ready:
 *
 *   REACT_APP_PWA_ENABLED=true
 *
 * then restart the frontend supervisor so CRA re-reads the env.
 */

const PWA_ENABLED =
  (process.env.REACT_APP_PWA_ENABLED || "").toLowerCase() === "true";


export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Skip in dev — CRA's dev server doesn't play well with SWs.
  if (window.location.hostname === "localhost") return;

  if (PWA_ENABLED) {
    registerPwaServiceWorker();
  } else {
    registerKillSwitchServiceWorker();
  }
}


// ── Mode B: PWA service worker (iter167) ─────────────────────────────


// Fire the `sw:update-available` window event so App.js can show a
// "New version available — Refresh" sonner toast. Idempotent —
// dispatching the same event twice in the same session simply
// re-shows the toast (sonner dedupes by id).
function notifyUpdateAvailable() {
  try {
    window.dispatchEvent(new CustomEvent("sw:update-available"));
  } catch { /* old browsers without CustomEvent — skip */ }
}


function registerPwaServiceWorker() {
  // iter186: once the user accepts the "Refresh" prompt, we send
  // SKIP_WAITING to the waiting worker. Activating it triggers
  // `controllerchange` — at which point we reload the page so it
  // runs against the fresh shell. Guarded by a session flag to
  // avoid reload loops (e.g. Chrome firing controllerchange on
  // every nav after the first activate).
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (sessionStorage.getItem("__sw_v3_reloaded")) return;
    sessionStorage.setItem("__sw_v3_reloaded", "1");
    window.location.reload();
  });

  // iter187: listen for SW_VERSION broadcasts from sw-v3.js and
  // stash on `window.__SW_VERSION`. Support can ask users to type
  // `__SW_VERSION` in DevTools and instantly know which build is
  // serving them — no need to dig through console logs that might
  // have scrolled away.
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data && event.data.type === "SW_VERSION") {
      window.__SW_VERSION = event.data.version;
    }
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw-v3.js", { scope: "/" })
      .then((reg) => {
        // iter187: if there's already a controller (e.g. on a hot
        // reload), ask it for its version directly so __SW_VERSION
        // is populated before the next activate broadcast.
        if (navigator.serviceWorker.controller) {
          try {
            navigator.serviceWorker.controller.postMessage({ type: "GET_VERSION" });
          } catch { /* ignore */ }
        }
        // If a v3 update is already waiting (e.g. another tab
        // installed it on a previous visit), surface the toast
        // right away instead of silently auto-applying. The user
        // can then click Refresh on their own timing.
        if (reg.waiting && navigator.serviceWorker.controller) {
          notifyUpdateAvailable();
        }
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // A new SW is installed AND there's already a
              // controller — that's an UPDATE, not the first install.
              // Notify the user; they choose when to apply.
              notifyUpdateAvailable();
            }
          });
        });
      })
      .catch((err) => {
        // SW failed — fall back to the kill-switch so we at least
        // clean any prior caches.
        console.warn("[sw-v3] registration failed, falling back:", err);
        purgeFromPage();
      });
  });
}


// ── Mode A: kill-switch (default, unchanged behaviour from iter138) ──


function registerKillSwitchServiceWorker() {
  // One-time reload after the destroyer SW activates. Guarded by a
  // session flag so we never get stuck in a reload loop.
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data && event.data.type === "SW_KILLED") {
      if (sessionStorage.getItem("__sw_killed_reloaded")) return;
      sessionStorage.setItem("__sw_killed_reloaded", "1");
      purgeFromPage().finally(() => window.location.reload());
    }
  });

  window.addEventListener("load", () => {
    // Register the destroyer so it can take over and self-uninstall.
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {
      // If registration fails, fall back to purging from the page.
      purgeFromPage();
    });

    // Belt-and-braces: also purge from the page after load in case the
    // destroyer SW has already activated in a previous session but the
    // browser is still serving cached assets.
    navigator.serviceWorker.getRegistrations().then((regs) => {
      if (regs.length === 0) {
        purgeFromPage();
      }
    }).catch(() => {});
  });
}


// ── Shared helpers ───────────────────────────────────────────────────


async function purgeFromPage() {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
  } catch { /* ignore */ }
  try {
    if (window.caches && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
    }
  } catch { /* ignore */ }
}


// Kept for backward compatibility with any caller that imports it.
export function applyServiceWorkerUpdate() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.getRegistration().then((reg) => {
    if (reg && reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
  }).catch(() => {});
}
